import React, { useEffect, useState } from "react";
import {
  IconBrain, IconRobot, IconMessage, IconCalendarStats,
  IconCurrencyDollar, IconUserCheck, IconCalendar,
} from "@tabler/icons-react";
import BlockSection, { KPISubgroup } from "./BlockSection";
import KPICard from "./KPICard";
import InsightsChart from "./InsightsChart";
import { fetchAIPerformance, exportInsightsXLSX } from "../services";

const AIPerformanceBlock = ({ filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartMode, setChartMode] = useState("messages"); // messages | appointments

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchAIPerformance(filters);
      setData(d);
    } catch (e) {
      setError("Não foi possível carregar este bloco.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [filters]);

  const k = data?.kpis || {};
  const chart = chartMode === "messages"
    ? data?.charts?.messages_ai_vs_operator
    : data?.charts?.appointments_ai_vs_manual;

  return (
    <BlockSection
      title="Meu agente IA está dando retorno?"
      subtitle="Quanto a IA produziu, quanto economizou, quanto gerou"
      icon={IconBrain}
      color="linear-gradient(135deg, #2bc155, #20c997)"
      loading={loading}
      error={error}
      onRetry={load}
      onExport={() => exportInsightsXLSX("ai_performance", filters)}
    >
      <KPISubgroup title="Comunicação" icon={IconMessage} color="#137ff7">
        <KPICard
          label="% Atendimento Automatizado"
          kpi={k.automation_pct}
          icon={IconRobot}
          color="linear-gradient(135deg, #2bc155, #20c997)"
          hint={
            k.automation_pct?.value === null
              ? "Sem mensagens de operador no período — cálculo não disponível"
              : "Mensagens IA ÷ (IA + Operador)"
          }
        />
        <KPICard
          label="Mensagens IA enviadas"
          kpi={k.messages_ai}
          icon={IconMessage}
          color="linear-gradient(135deg, #137ff7, #1ea7c5)"
        />
        <KPICard
          label="Mensagens Recebidas pela IA"
          kpi={k.messages_received_by_ai}
          icon={IconMessage}
          hint="Mensagens de cliente que a IA processou"
        />
        <KPICard
          label="Mensagens do Operador"
          kpi={k.messages_operator}
          icon={IconMessage}
          color="linear-gradient(135deg, #f59e0b, #d97706)"
          hint="Mensagens enviadas via Chat UI por atendente humano"
        />
      </KPISubgroup>

      <KPISubgroup title="Agendamentos" icon={IconCalendar} color="#16a34a">
        <KPICard
          label="Agendamentos via IA"
          kpi={k.appointments_ai}
          icon={IconCalendarStats}
          color="linear-gradient(135deg, #2bc155, #20c997)"
        />
        <KPICard
          label="% Agendamentos via IA"
          kpi={k.appointments_ai_pct}
          icon={IconCalendarStats}
        />
        <KPICard
          label="Receita gerada pela IA"
          kpi={k.revenue_ai}
          icon={IconCurrencyDollar}
          color="linear-gradient(135deg, #16a34a, #15803d)"
          hint="Agendamentos realizados criados pela IA"
        />
      </KPISubgroup>

      <KPISubgroup title="CRM" icon={IconUserCheck} color="#a855f7">
        <KPICard
          label="Clientes vindos do WhatsApp"
          kpi={k.conversions_whatsapp}
          icon={IconUserCheck}
          hint="Contatos com origem WhatsApp que viraram cliente no período"
        />
      </KPISubgroup>

      {chart && (
        <div className="insights-chart insights-chart--wide">
          <div className="insights-chart__head">
            <h3>IA vs Humano (diário)</h3>
            <div className="insights-chart__toggle">
              <button
                className={chartMode === "messages" ? "active" : ""}
                onClick={() => setChartMode("messages")}
              >Mensagens</button>
              <button
                className={chartMode === "appointments" ? "active" : ""}
                onClick={() => setChartMode("appointments")}
              >Agendamentos</button>
            </div>
          </div>
          <InsightsChart
            type="line"
            categories={chart.categories || []}
            series={chart.series || []}
            colors={["#2bc155", "#137ff7"]}
          />
        </div>
      )}
    </BlockSection>
  );
};

export default AIPerformanceBlock;
